import React from 'react';
import {
  SOCIAL_GLYPHS,
  type SocialGlyph,
  type SocialGlyphToken,
} from './glyphs';

export type { SocialGlyphToken } from './glyphs';
export { SOCIAL_GLYPHS } from './glyphs';

/** `mono` para a fileira do cabeçalho, `brand` para o badge sobre a foto. */
export type SocialIconVariant = 'mono' | 'brand';

export interface SocialIconProps
  extends Omit<React.SVGProps<SVGSVGElement>, 'children'> {
  network: SocialGlyphToken;
  variant?: SocialIconVariant;
  size?: number | string;
  /** Nome da rede para leitores de tela. Sem ele o ícone fica `aria-hidden`. */
  label?: string;
}

/* Rótulos com a grafia de cada marca — é o que o leitor de tela vai anunciar. */
export const SOCIAL_LABELS: Record<SocialGlyphToken, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  linkedin: 'LinkedIn',
  x: 'X',
  youtube: 'YouTube',
  spotify: 'Spotify',
  tiktok: 'TikTok',
  flickr: 'Flickr',
  threads: 'Threads',
  amazonmusic: 'Amazon Music',
  deezer: 'Deezer',
};

/* Ordem da fileira do cabeçalho, como no design. */
export const SOCIAL_ORDER: SocialGlyphToken[] = [
  'facebook',
  'instagram',
  'whatsapp',
  'linkedin',
  'x',
  'threads',
  'youtube',
  'spotify',
  'tiktok',
  'flickr',
  'amazonmusic',
  'deezer',
];

export const isSocialGlyphToken = (
  value: string | undefined,
): value is SocialGlyphToken => Boolean(value && value in SOCIAL_GLYPHS);

/**
 * Ícone de rede social.
 *
 * `mono` pinta com `currentColor`, então a cor vem da classe de quem usa
 * (`text-blue-900` no cabeçalho). `brand` usa a cor da marca e assenta o ícone
 * sobre um retângulo branco — sem ele, os ícones de recorte deixariam a foto
 * do card aparecer no vão do símbolo.
 */
export const SocialIcon: React.FC<SocialIconProps> = ({
  network,
  variant = 'mono',
  size = 32,
  label,
  ...props
}) => {
  const glyph = SOCIAL_GLYPHS[network] as SocialGlyph | undefined;
  // `useId` porque o gradiente do Instagram vira um `<defs>` no documento: com
  // id fixo, dois ícones na mesma página colidiriam e o segundo herdaria o
  // gradiente do primeiro.
  const gradientId = `mpf-social-grad-${React.useId()}`;

  if (!glyph) return null;

  // `isBrand` também vira o atributo `data-brand-icon` no <svg>: a SC 1.4.11
  // do WCAG isenta logotipo e nome de marca do contraste mínimo, e a auditoria
  // (`scripts/a11y/contrast_audit.py`) usa esse atributo para separar essas
  // ocorrências das violações reais.
  const isBrand = variant === 'brand';
  const frameFill =
    glyph.gradient && isBrand
      ? `url(#${gradientId})`
      : isBrand
        ? glyph.brand
        : 'currentColor';

  const a11y = label
    ? { role: 'img' as const, 'aria-label': label }
    : { 'aria-hidden': true as const, focusable: 'false' as const };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-brand-icon={isBrand ? '' : undefined}
      {...a11y}
      {...props}
    >
      {glyph.gradient && isBrand ? (
        <defs>
          <radialGradient
            id={gradientId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform={glyph.gradient.transform}
          >
            {glyph.gradient.stops.map((stop) => (
              <stop
                key={stop.offset}
                offset={stop.offset}
                stopColor={stop.color}
              />
            ))}
          </radialGradient>
        </defs>
      ) : null}

      {/* Fundo branco só no badge, e só na família de recorte: nos ícones de
          quadro o próprio quadro já é opaco. `rx` um pouco menor que o do
          quadro para não escapar pelos cantos. */}
      {isBrand && glyph.family === 'cutout' ? (
        <rect width="32" height="32" rx="9.6" fill="#FFFFFF" />
      ) : null}

      <path d={glyph.frame} fill={frameFill} />

      {/* O símbolo é o negativo do quadro. No badge colorido ele é branco,
          como no material das marcas. Já no ícone monocromático o quadro é
          `currentColor` — que no tema escuro é claro —, então um símbolo
          branco fixo desaparecia dentro dele e o ícone virava um quadrado
          sólido (acontecia com X, Amazon Music, Deezer e Flickr, as quatro
          famílias `framed`). Usar a cor da superfície reproduz o vazado que
          a família `cutout` consegue de graça, e vale nos dois temas. */}
      {glyph.glyphs?.map((d) => (
        <path
          key={d.slice(0, 24)}
          transform={glyph.glyphTransform}
          d={d}
          fill={isBrand ? '#FFFFFF' : 'var(--surface)'}
        />
      ))}
    </svg>
  );
};

export default SocialIcon;
